import Page from 'attendee/theme/Page'
import {getLegalBodyHtml} from 'attendee/legalDocuments/legalBodyRegistry'
import type {LegalSlug} from 'attendee/legalDocuments/types'
import {useI18n} from 'i18n/I18nContext'
import {withLocalePath} from 'i18n/localePaths'
import React, {useMemo} from 'react'
import {Helmet} from 'react-helmet'
import {DEFAULT_OG_IMAGE, SITE_ORIGIN} from 'seo/siteMeta'
import styled from 'styled-components'

export type {LegalSlug} from 'attendee/legalDocuments/types'

export interface LegalDocumentPageProps {
  slug: LegalSlug
  /** Absolute app path, e.g. `/terms-and-conditions` */
  routePath: string
  /** i18n key prefix, e.g. `legal.terms` */
  i18nPrefix: string
}

export function LegalDocumentPage(props: LegalDocumentPageProps) {
  const {slug, routePath, i18nPrefix} = props
  const {locale, t} = useI18n()
  const bodyHtml = useMemo(() => getLegalBodyHtml(slug, locale), [slug, locale])

  const pageUrl = `${SITE_ORIGIN}${withLocalePath(locale, routePath)}`

  return (
    <Page>
      <>
        <Helmet>
          <title>{t(`${i18nPrefix}.pageTitle`)}</title>
          <meta
            name="description"
            content={t(`${i18nPrefix}.metaDescription`)}
          />
          <link rel="canonical" href={pageUrl} />
          <meta property="og:title" content={t(`${i18nPrefix}.ogTitle`)} />
          <meta
            property="og:description"
            content={t(`${i18nPrefix}.ogDescription`)}
          />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:image" content={DEFAULT_OG_IMAGE} />
          <meta property="og:image:secure_url" content={DEFAULT_OG_IMAGE} />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
        </Helmet>

        <section className="contact_section dark_light_class pt-55 pb-55">
          <div className="container">
            <div className="section__title-wrapper mb-80">
              <span className="section__back-title">
                {t(`${i18nPrefix}.heroBack`)}
              </span>
              <span className="section__subtitle">
                {t(`${i18nPrefix}.heroSubtitle`)}
              </span>
              <h1
                className="section__title"
                dangerouslySetInnerHTML={{
                  __html: t(`${i18nPrefix}.heroTitleHtml`),
                }}
              />
            </div>

            <ContentCard className="row">
              <div className="col-12">
                {bodyHtml ? (
                  <ArticleBody
                    className="legal-doc"
                    dangerouslySetInnerHTML={{__html: bodyHtml}}
                  />
                ) : (
                  <StatusLine>{t('legal.loadError')}</StatusLine>
                )}
              </div>
            </ContentCard>
          </div>
        </section>
      </>
    </Page>
  )
}

const ContentCard = styled.div`
  & > div {
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 10px;
    padding: 32px;
  }
`

const StatusLine = styled.p`
  margin: 0;
  opacity: 0.85;
`

const ArticleBody = styled.div`
  color: var(--bs-body-color);

  .meta {
    margin: 0 0 12px 0;
    opacity: 0.75;
    font-size: 0.9rem;
  }

  .intro {
    margin: 0 0 24px 0;
    font-size: 1.02rem;
    opacity: 0.92;
    line-height: 1.7;
    padding: 16px;
    background: rgba(0, 0, 0, 0.025);
    border-left: 3px solid rgba(0, 0, 0, 0.12);
    border-radius: 6px;
  }

  h3 {
    margin-top: 2rem;
    margin-bottom: 0.6rem;
    font-size: 1.1rem;
    font-weight: 600;
  }

  p,
  li {
    line-height: 1.65;
  }

  ul,
  ol {
    padding-left: 1.25rem;
    margin: 0.5rem 0 1rem;
  }

  & > ul > li,
  & > ol > li,
  ul li,
  ol li {
    margin: 0.5rem 0;
  }

  a {
    word-break: break-word;
  }
`
